import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {PublicationAccessService} from "../../landing-publication/publication-access.service";
import {environment} from "environments/environment";
import {UserService} from "app/core/user/user.service";
import {PublicationMessagesService} from "../../landing-publication/publication-messages.service";
import {HttpClient} from "@angular/common/http";
import {ToastrService} from "ngx-toastr";
import {combineLatest, merge, of} from "rxjs";
import {filter, map} from "rxjs/operators";
import {NavigationEnd, Router} from "@angular/router";
import {EventMessagesService} from "@store/event-messages.service";

export interface ILinkedPublicationRow {
    publicationUuid: string;
    title: string | null;
    published: boolean;
    isDefault: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-publication-debug',
    templateUrl: './publication-debug.component.html',
    styleUrls: ['./publication-debug.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PublicationDebugComponent implements OnInit {

    clientAppUrl = environment.clientAppUrl;
    private readonly apiUrl = environment.apiUrl;

    publicationUuid: string;

    linkedPublications: ILinkedPublicationRow[] = [];
    loadingLinkedPublications = false;
    private linkedPubRequestSeq = 0;
    private lastLinkedPubSurveyIdsKey: string | null = null;
    /** Après échec API, évite de boucler si debugInfos réémet souvent avec les mêmes ids. */
    private linkedPubFailedSurveyIdsKey: string | null = null;

    extValuesPerGroup = {};

    electedGroupIndex: string[] = [];

    debugInfos: any = {};

    surveyIds: string[] = [];

    // <!--            "sqlRule": "(\n               2610_22293\n                !=\n                '** NOT SET FOR USER **' ) AND (\n                joyce_random\n                =\n                '1' )",-->
    // <!--            "comId": 1075,-->
    // <!--            "publicationUuid": "ecded93d-1911-41c9-9f60-a0b705f198ed",-->
    // <!--            "groupId": "78b78114-92a5-4eaa-bd3f-e6f42e563449",-->
    // <!--            "groupTitle": "A répondu au 2610",-->
    // <!--            "fields": [-->
    // <!--            "2610_22293",-->
    // <!--            "joyce_random"-->
    // <!--            ],-->


    evalResultDebug: {
        sql2: any,
        sql3: {
            result: {
                sqlRule: string;
                comId: number,
                publicationUuid: string,
                groupId: string,
                groupTitle: string,
                fields: string[],
            }[],
        },
        sqlFake?: {
            result: {
                pubUuid: string;
                groupId: string;
            }[]
        },
    };

    constructor(
        public userService: UserService,
        public publicationAccessService: PublicationAccessService,
        public publicationMessagesService: PublicationMessagesService,
        private cdr: ChangeDetectorRef,
        private http: HttpClient,
        private toastr: ToastrService,
        private router: Router,
        private eventMessagesService: EventMessagesService,
    ) {
    }

    ngOnInit(): void {

        const navigationTick$ = merge(
            of(this.router.url),
            this.router.events.pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                map(() => this.router.url),
            ),
        );

        combineLatest([
            this.publicationAccessService.debugInfos$,
            this.publicationAccessService.allSurveyIds$,
            navigationTick$,
        ]).pipe(untilDestroyed(this)).subscribe(([debugInfo, allSurveyIdsFromLanding, routerUrl]) => {

            this.surveyIds = this.resolveSurveyIdsFromDebug(debugInfo, allSurveyIdsFromLanding, routerUrl);

            this.publicationUuid = this.publicationAccessService.pubUuid;

            this.debugInfos = debugInfo;

            this.evalResultDebug = debugInfo?.evaluation?.evalResult?.debugInfos;

            this.electedGroupIndex = [];

            this.evalResultDebug?.sql3?.result.forEach((aGroup) => {

                this.extValuesPerGroup[aGroup.groupId] = {};

                aGroup.fields.forEach((key) => {
                    this.extValuesPerGroup[aGroup.groupId][key] = this.userService.user.extProfiles?.[key] || '** NOT SET FOR USER **';
                });

            });

            this.evalResultDebug?.sqlFake?.result?.forEach((ar) => {
                this.electedGroupIndex.push(ar.groupId);
            });

            this.loadLinkedPublicationsForSurveyIds();

            this.cdr.markForCheck();

        });

    }

    /**
     * Les ids viennent de plusieurs flux (chargement landing, mode debug API, évaluation).
     * Ordre important côté service : allSurveyIds$ est mis à jour avant debugInfos$ pour que le repli soit fiable.
     */
    /**
     * Sondage courant dans l’URL (ex. /survey/2610, /survey/preview/2610, ?surveyId=…).
     * C’est la source fiable quand on est sur la page sondage ; le resolver met aussi eventMessagesService.surveyId.
     */
    private extractSurveyIdsFromRouterUrl(routerUrl: string): string[] {
        const ids = new Set<string>();
        const full = routerUrl || '';
        const path = full.split('?')[0];
        const previewOrEdit = path.match(/\/survey\/(?:preview|editResponse)\/(\d+)/);
        if (previewOrEdit?.[1]) {
            ids.add(previewOrEdit[1]);
        }
        if (ids.size === 0) {
            const direct = path.match(/\/survey\/(\d+)/);
            if (direct?.[1]) {
                ids.add(direct[1]);
            }
        }
        const q = full.split('?')[1];
        if (q) {
            const sid = new URLSearchParams(q).get('surveyId');
            if (sid != null && sid !== '' && Number.isFinite(Number(sid))) {
                ids.add(String(Number(sid)));
            }
        }
        return Array.from(ids);
    }

    private resolveSurveyIdsFromDebug(debugInfo: any, allSurveyIdsFromLanding: number[], routerUrl: string): string[] {
        const seen = new Set<string>();
        const add = (raw: unknown) => {
            if (raw == null) {
                return;
            }
            if (Array.isArray(raw)) {
                raw.forEach((id) => add(id));
                return;
            }
            const n = Number(raw);
            if (!Number.isNaN(n)) {
                seen.add(String(n));
            }
        };

        add(this.extractSurveyIdsFromRouterUrl(routerUrl));
        const evSid = this.eventMessagesService.surveyId;
        if (Number.isFinite(evSid as number)) {
            add(evSid);
        }

        add(debugInfo?.publication?.surveys?.ids);
        add(allSurveyIdsFromLanding);
        add(Object.keys(debugInfo?.evaluation?.surveysInfo || {}));
        add(this.publicationAccessService.electedSurveys$.value?.index);

        const evalSurveyRows = debugInfo?.evaluation?.evalResult?.surveyIds;
        if (Array.isArray(evalSurveyRows)) {
            evalSurveyRows.forEach((row: { surveyId?: number | string }) => add(row?.surveyId));
        }

        return Array.from(seen);
    }

    private loadLinkedPublicationsForSurveyIds(): void {
        const ids = (this.surveyIds || [])
            .map((id) => Number(id))
            .filter((n) => !Number.isNaN(n));
        const pubUuid = this.publicationAccessService.pubUuid;
        /** Requête API si on a des ids OU un pubUuid (le serveur résout les ids depuis MySQL). */
        const canQueryApi = ids.length > 0 || !!pubUuid;
        if (!canQueryApi) {
            this.linkedPublications = this.mergeLinkedPublicationsWithCurrent([]);
            this.lastLinkedPubSurveyIdsKey = null;
            this.linkedPubFailedSurveyIdsKey = null;
            return;
        }
        const key = JSON.stringify({ ids, pubUuid: pubUuid || null });
        if (key === this.lastLinkedPubSurveyIdsKey) {
            return;
        }
        if (key === this.linkedPubFailedSurveyIdsKey) {
            return;
        }

        const reqId = ++this.linkedPubRequestSeq;
        this.loadingLinkedPublications = true;
        this.http
            .post(
                `${this.apiUrl}/appCommunity/actions`,
                {
                    action: 'getPublicationsLinkedToSurveys',
                    params: { surveyIds: ids, pubUuid },
                },
            )
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res: any) => {
                    if (reqId !== this.linkedPubRequestSeq) {
                        return;
                    }
                    const rawList =
                        res?.data?.datas?.publications
                        ?? res?.data?.publications
                        ?? res?.publications
                        ?? [];
                    const list = this.mergeLinkedPublicationsWithCurrent(rawList);
                    this.linkedPublications = this.sortLinkedPublications(list);
                    this.lastLinkedPubSurveyIdsKey = key;
                    this.linkedPubFailedSurveyIdsKey = null;
                    this.loadingLinkedPublications = false;
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    if (reqId !== this.linkedPubRequestSeq) {
                        return;
                    }
                    this.linkedPublications = this.mergeLinkedPublicationsWithCurrent([]);
                    this.linkedPubFailedSurveyIdsKey = key;
                    this.loadingLinkedPublications = false;
                    this.toastr.error(
                        err?.error?.message || 'getPublicationsLinkedToSurveys',
                        'Publications',
                    );
                    this.cdr.markForCheck();
                },
            });
    }

    /**
     * Garantit au moins un lien vers la publication d’atterrissage (sans dépendre d’une « publication par défaut »).
     */
    private mergeLinkedPublicationsWithCurrent(rows: ILinkedPublicationRow[]): ILinkedPublicationRow[] {
        const cur = this.publicationAccessService.pubUuid;
        if (!cur) {
            return rows || [];
        }
        const list = [...(rows || [])];
        if (list.some((r) => r.publicationUuid === cur)) {
            return list;
        }
        const pub = this.publicationAccessService.publication$.value;
        const publishedRaw = pub?.generalInfo?.published;
        list.unshift({
            publicationUuid: cur,
            title: pub?.generalInfo?.title ?? null,
            published: !!publishedRaw,
            isDefault: false,
        });
        return list;
    }

    private sortLinkedPublications(rows: ILinkedPublicationRow[]): ILinkedPublicationRow[] {
        const cur = this.publicationUuid;
        return [...rows].sort((a, b) => {
            const aCur = a.publicationUuid === cur ? 1 : 0;
            const bCur = b.publicationUuid === cur ? 1 : 0;
            if (aCur !== bCur) {
                return bCur - aCur;
            }
            const ta = (a.title || a.publicationUuid || '').toLowerCase();
            const tb = (b.title || b.publicationUuid || '').toLowerCase();
            return ta.localeCompare(tb);
        });
    }

    checkAgain() {
      this.linkedPubFailedSurveyIdsKey = null;
      this.lastLinkedPubSurveyIdsKey = null;
      this.publicationAccessService.evaluatePublicationRules();
    }

}
